<template>
  <div>
    <!-- Hidden inputs for form submission -->
    <input type="hidden" :name="props.inputNameName" v-model="nameInput" />
    <input type="hidden" :name="props.inputNameEmail" v-model="emailInput" />

    <!-- Card Display -->
    <div>
      <div class="text-sm md:text-base font-medium text-gray-700 mb-2">
        {{ props.title || "BCC Contact" }}
      </div>
      <div
        class="flex items-center bg-white border border-gray-200 rounded-lg overflow-hidden transition-all duration-200 hover:shadow-sm"
        :class="{ 'border-l-4 border-l-cs-main-500': nameInput || emailInput }"
      >
        <!-- Contact Info -->
        <div class="flex-1 py-3 px-2 min-w-0">
          <div v-if="nameInput || emailInput" class="flex flex-col">
            <span
              class="text-sm md:text-base font-semibold text-gray-800 truncate"
              >{{ nameInput || "No Name" }}</span
            >
            <span class="text-xs md:text-sm text-gray-500 truncate">{{
              emailInput || "No Email"
            }}</span>
          </div>
          <div v-else class="flex flex-col">
            <span class="text-sm md:text-base text-gray-400 italic">{{
              props.title || "Select BCC Contact"
            }}</span>
            <span class="text-xs md:text-sm text-gray-300"
              >Click edit to add contact</span
            >
          </div>
        </div>

        <!-- Edit Button -->
        <button
          type="button"
          class="flex items-center justify-center w-12 h-full py-4 text-cs-main-500 hover:bg-gray-50 transition-colors duration-200"
          @click="openContactBook"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Only show warning for BCC recipients -->
    <p
      v-if="isDuplicateSubmitter && showWarning"
      class="text-xs text-amber-600 mt-2"
    >
      Signers will receive a copy upon completion; no need to add them to BCC
    </p>

    <!-- Warning input validation -->
    <div v-if="inputBccErrors.length" class="text-red-500 text-xs mt-2">
      <div v-for="(err, idx) in inputBccErrors" :key="idx">
        {{ err }}
      </div>
    </div>

    <!-- Toast Notification -->
    <Teleport to="body">
      <transition name="toast-fade">
        <div
          v-if="toastMessage"
          class="fixed top-6 right-6 bg-green-500 text-white px-5 py-3 rounded-lg shadow-lg text-sm font-medium flex items-center gap-2"
          style="z-index: 1000010"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M20 6L9 17l-5-5" />
          </svg>
          {{ toastMessage }}
        </div>
      </transition>
    </Teleport>

    <!-- Contact Modal - Teleported to body to escape parent modal stacking context -->
    <Teleport to="body">
      <div
        v-if="isModalOpen"
        id="contactModal"
        class="fixed inset-0 flex items-center justify-center p-4"
        style="z-index: 999999"
      >
        <div
          class="fixed inset-0 bg-black bg-opacity-50"
          @click="isModalOpen = false"
        ></div>
        <div
          class="relative bg-white rounded-xl shadow-xl pt-4 pb-6 px-6 max-h-[90vh] overflow-y-auto w-full max-w-xl"
          style="z-index: 1000000"
        >
          <!-- Modal Header -->
          <div
            class="flex justify-between items-center border-b border-gray-100 pb-3 mb-4"
          >
            <span class="text-lg font-semibold text-gray-800">{{
              props.title || "Select BCC Contact"
            }}</span>
            <a
              href="#"
              class="text-2xl text-gray-400 hover:text-gray-600 transition-colors"
              @click.prevent="isModalOpen = false"
              >&times;</a
            >
          </div>

          <!-- Manual Entry Form -->
          <div class="pt-2">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div class="form-control">
                <label class="label py-1">
                  <span class="label-text text-xs text-gray-500"
                    >Name <span class="text-red-500">*</span></span
                  >
                </label>
                <input
                  ref="nameInputRef"
                  v-model="nameInput"
                  type="text"
                  class="input input-bordered w-full text-sm bg-white border-gray-200 focus:border-cs-main-500"
                  :class="{
                    'border-red-400':
                      nameInput !== null &&
                      nameInput.trim() === '' &&
                      nameTouched,
                  }"
                  placeholder="Enter name"
                  data-email-input="true"
                  required
                  @input="checkForDuplicateSubmitter"
                  @blur="
                    nameTouched = true;
                    validateInputs();
                  "
                />
                <div
                  v-if="nameTouched && nameInput.trim() === ''"
                  class="text-red-500 text-xs mt-1"
                >
                  Name is required.
                </div>
              </div>
              <div class="form-control">
                <label class="label py-1">
                  <span class="label-text text-xs text-gray-500"
                    >Email <span class="text-red-500">*</span></span
                  >
                </label>
                <input
                  ref="emailInputRef"
                  v-model="emailInput"
                  type="text"
                  class="input input-bordered w-full text-sm bg-white border-gray-200 focus:border-cs-main-500"
                  :class="{ 'border-red-400': emailTouched && !isEmailValid }"
                  placeholder="Enter email"
                  data-email-input="true"
                  required
                  @input="checkForDuplicateSubmitter"
                  @blur="
                    emailTouched = true;
                    validateInputs();
                  "
                />
                <div
                  v-if="emailTouched && !isEmailValid"
                  class="text-red-500 text-xs mt-1"
                >
                  Valid email is required.
                </div>
              </div>
            </div>
          </div>

          <!-- Modal Actions -->
          <div
            class="flex justify-end gap-3 mt-4 pt-4 border-t border-gray-100"
          >
            <button
              type="button"
              class="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 border border-gray-200 rounded-lg hover:bg-gray-200 transition-colors"
              @click="isModalOpen = false"
            >
              Cancel
            </button>
            <button
              type="button"
              class="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors"
              :class="
                isFormValid
                  ? 'bg-cs-main-500 hover:bg-cs-main-600'
                  : 'bg-gray-300 cursor-not-allowed'
              "
              :disabled="!isFormValid"
              @click="confirmSelection"
            >
              Confirm
            </button>
          </div>

          <!-- Existing Contacts List -->
          <div class="mt-6 pt-4 border-t border-gray-100">
            <div class="flex items-center justify-between mb-3">
              <h4 class="text-sm font-medium text-gray-600">
                Existing Contacts
              </h4>
              <button
                type="button"
                class="btn btn-xs bg-cs-main-500 text-white hover:bg-cs-main-600 rounded-lg px-3 py-1 transition-colors duration-300 flex items-center gap-1 normal-case text-xs"
                @click="refreshLocalContacts"
              >
                <IconRefresh size="14" class="opacity-80" />
                Refresh
              </button>
            </div>
            <input
              v-model="searchKey"
              type="text"
              class="input input-sm input-bordered w-full mb-3 bg-white border-gray-200 focus:border-cs-main-500"
              placeholder="Search contacts..."
            />
            <div
              class="relative overflow-x-auto rounded-lg border border-gray-100 max-h-48 overflow-y-auto"
            >
              <table class="w-full text-sm text-left">
                <thead
                  class="text-xs text-gray-500 uppercase bg-gray-50 sticky top-0"
                >
                  <tr>
                    <th scope="col" class="px-4 py-2">Name</th>
                    <th scope="col" class="px-4 py-2">Email</th>
                    <th scope="col" class="px-4 py-2 text-center w-24">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <!-- Edit Form Row -->
                  <tr
                    v-if="editingContactIndex !== null"
                    class="border-b border-gray-50 bg-blue-50"
                  >
                    <td class="px-4 py-2">
                      <input
                        v-model="editForm.name"
                        type="text"
                        class="input input-xs input-bordered w-full bg-white border-gray-200 focus:border-cs-main-500"
                        placeholder="Name"
                        @click.stop
                      />
                      <div
                        v-if="editContactErrors.name"
                        class="text-red-500 text-xs mt-1"
                      >
                        {{ editContactErrors.name }}
                      </div>
                    </td>
                    <td class="px-4 py-2">
                      <input
                        v-model="editForm.email"
                        type="text"
                        class="input input-xs input-bordered w-full bg-white border-gray-200 focus:border-cs-main-500"
                        placeholder="Email"
                        @click.stop
                      />
                      <div
                        v-if="editContactErrors.email"
                        class="text-red-500 text-xs mt-1"
                      >
                        {{ editContactErrors.email }}
                      </div>
                    </td>
                    <td class="px-4 py-2 text-center">
                      <div class="flex items-center justify-center gap-1">
                        <button
                          type="button"
                          class="p-1 text-green-600 hover:bg-green-50 rounded transition-colors"
                          title="Save Changes"
                          @click.stop="onSaveEditContact"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          >
                            <path d="M20 6L9 17l-5-5" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          class="p-1 text-gray-400 hover:bg-gray-100 rounded transition-colors"
                          title="Cancel"
                          @click.stop="cancelEditContact"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          >
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                  <!-- Contact Rows -->
                  <tr
                    v-for="(contact, index) in allContacts"
                    :key="`contact-${index}`"
                    class="border-b border-gray-50 hover:cursor-pointer hover:bg-cs-main-50 transition-colors"
                    :class="{ 'bg-yellow-50': editingContactIndex === index }"
                    @click.stop="onSelectedContact(contact, $event)"
                  >
                    <td class="px-4 py-2 text-gray-700">{{ contact.name }}</td>
                    <td class="px-4 py-2 text-gray-500">{{ contact.email }}</td>
                    <td class="px-4 py-2 text-center">
                      <div class="flex items-center justify-center gap-1">
                        <template v-if="contact.source === 'contact_book'">
                          <button
                            type="button"
                            class="p-1 text-blue-500 hover:bg-blue-50 rounded transition-colors"
                            title="Edit Contact"
                            @click.stop="onEditContact(contact, index)"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              stroke-width="2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            >
                              <path
                                d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"
                              />
                            </svg>
                          </button>
                          <button
                            type="button"
                            class="p-1 text-red-400 hover:bg-red-50 rounded transition-colors"
                            title="Delete Contact"
                            @click.stop="onDeleteContact(contact, index)"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              stroke-width="2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            >
                              <polyline points="3 6 5 6 21 6" />
                              <path
                                d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"
                              />
                            </svg>
                          </button>
                        </template>
                        <span
                          v-else-if="contact.source === 'contact'"
                          class="text-gray-400 text-xs italic"
                          title="This contact is managed by your organization and cannot be edited."
                          >Managed</span
                        >
                        <template v-else>
                          <span
                            class="text-amber-400 text-xs italic inline-flex items-center gap-0.5 flex-shrink-0"
                            title="This contact is temporary and will be removed after refresh. It is not saved to your contact list."
                            ><svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="inline-block"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>Not saved</span
                          >
                          <button
                            type="button"
                            class="p-1 text-red-400 hover:bg-red-50 rounded transition-colors"
                            title="Remove Contact?"
                            @click.stop="onDeleteTempContact(index)"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              stroke-width="2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            >
                              <polyline points="3 6 5 6 21 6" />
                              <path
                                d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"
                              />
                            </svg>
                          </button>
                        </template>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Add New Contact Section -->
          <div class="mt-6 pt-4 border-t border-gray-100">
            <h4 class="text-sm font-medium text-gray-600 mb-3">
              Add New Contact
            </h4>
            <form @submit.prevent="onSubmitNewContact">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div class="form-control">
                  <input
                    v-model="newContact.name"
                    type="text"
                    class="input input-sm input-bordered w-full bg-white border-gray-200 focus:border-cs-main-500"
                    placeholder="Name"
                    required
                  />
                  <div
                    v-if="inputContactErrors.name"
                    class="text-red-500 text-xs mt-1"
                  >
                    {{ inputContactErrors.name }}
                  </div>
                </div>
                <div class="form-control">
                  <input
                    v-model="newContact.email"
                    type="email"
                    class="input input-sm input-bordered w-full bg-white border-gray-200 focus:border-cs-main-500"
                    placeholder="Email"
                    required
                  />
                  <div
                    v-if="inputContactErrors.email"
                    class="text-red-500 text-xs mt-1"
                  >
                    {{ inputContactErrors.email }}
                  </div>
                </div>
              </div>
              <button
                type="submit"
                class="btn btn-sm bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg px-4 normal-case"
              >
                Save Contact
              </button>
            </form>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Delete Confirmation Modal -->
    <Teleport to="body">
      <div
        v-if="isDeleteModalOpen"
        class="fixed inset-0 flex items-center justify-center p-4"
        style="z-index: 1000010"
      >
        <div
          class="fixed inset-0 bg-black bg-opacity-50"
          @click="isDeleteModalOpen = false"
        ></div>
        <div
          class="relative bg-white rounded-xl shadow-xl p-6 w-full max-w-sm"
          style="z-index: 1000011"
        >
          <div class="text-center">
            <div
              class="mx-auto flex items-center justify-center w-12 h-12 rounded-full bg-red-100 mb-4"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="text-red-500"
              >
                <polyline points="3 6 5 6 21 6" />
                <path
                  d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"
                />
              </svg>
            </div>
            <h3 class="text-lg font-semibold text-gray-800 mb-2">
              Delete Contact
            </h3>
            <p class="text-sm text-gray-500 mb-6">
              Are you sure you want to delete this contact?<br />
              This action cannot be undone.
            </p>
            <div class="flex justify-center gap-3">
              <button
                type="button"
                class="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 border border-gray-200 rounded-lg hover:bg-gray-200 transition-colors"
                @click="isDeleteModalOpen = false"
              >
                Cancel
              </button>
              <button
                type="button"
                class="px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors"
                @click="onConfirmDelete"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Delete Temporary Contact Confirmation Modal -->
    <Teleport to="body">
      <div
        v-if="isDeleteTempModalOpen"
        class="fixed inset-0 flex items-center justify-center p-4"
        style="z-index: 1000010"
      >
        <div
          class="fixed inset-0 bg-black bg-opacity-50"
          @click="isDeleteTempModalOpen = false"
        ></div>
        <div
          class="relative bg-white rounded-xl shadow-xl p-6 w-full max-w-sm"
          style="z-index: 1000011"
        >
          <div class="text-center">
            <div
              class="mx-auto flex items-center justify-center w-12 h-12 rounded-full bg-red-100 mb-4"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="text-red-500"
              >
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            </div>
            <h3 class="text-lg font-semibold text-gray-800 mb-2">
              Remove contact?
            </h3>
            <p class="font-medium text-gray-800 mb-2">
              "{{ tempContactToDelete?.name || tempContactToDelete?.email }}"
            </p>
            <p class="text-sm text-gray-500 mb-6">
              This contact is temporary and will be removed after refresh. If needed again, it must be re-entered manually.
            </p>
            <div class="flex justify-center gap-3">
              <button
                type="button"
                class="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 border border-gray-200 rounded-lg hover:bg-gray-200 transition-colors"
                @click="isDeleteTempModalOpen = false"
              >
                Cancel
              </button>
              <button
                type="button"
                class="px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors"
                @click="onConfirmDeleteTemp"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { debounceFilter, watchWithFilter } from "@vueuse/core";
import { IconRefresh } from "@tabler/icons-vue";
import { defineProps, ref, watch, onMounted, onUnmounted, computed } from "vue";

const props = defineProps([
  "title",
  "input-name-name",
  "inputNameEmail",
  "email",
]);

const emailInput = ref("");
const nameInput = ref("");
const searchKey = ref("");
const isModalOpen = ref(false);
const allContacts = ref([]);
let checkTimeoutId = null;
const isDuplicateSubmitter = ref(false);
const isDuplicateName = ref(false);
const nameInputRef = ref(null);
const emailInputRef = ref(null);
const nameTouched = ref(false);
const emailTouched = ref(false);
const newContact = ref({
  name: "",
  email: "",
});
const inputContactErrors = ref({
  name: "",
  email: "",
});
const inputBccErrors = ref([]);

// Edit contact state
const editingContactIndex = ref(null);
const editForm = ref({ name: "", email: "", id: null });
const editContactErrors = ref({ name: "", email: "" });
const editOriginalEmail = ref("");

// Delete contact state
const isDeleteModalOpen = ref(false);
const contactToDelete = ref(null);
const contactToDeleteIndex = ref(null);

// Delete temp contact state
const isDeleteTempModalOpen = ref(false);
const tempContactToDelete = ref(null);
const tempContactToDeleteIndex = ref(null);

// Toast state
const toastMessage = ref("");
let toastTimeoutId = null;

// Computed property to determine if we should show the warning
// Note: BCC component uses 'real_bcc_recipients' input name
const showWarning = computed(() => {
  return props.inputNameEmail?.includes("real_bcc_recipients");
});

// Computed property to check if email is valid
const isEmailValid = computed(() => {
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailPattern.test(emailInput.value.trim());
});

// Computed property to check if form is valid (name + email required)
const isFormValid = computed(() => {
  return nameInput.value.trim() !== "" && isEmailValid.value;
});

// Sort contacts alphabetically by name (A-Z), then by email as tiebreaker
const sortContacts = (contacts) => {
  return contacts.sort((a, b) => {
    const nameA = (a.name || "").toLowerCase();
    const nameB = (b.name || "").toLowerCase();
    if (nameA < nameB) return -1;
    if (nameA > nameB) return 1;
    const emailA = (a.email || "").toLowerCase();
    const emailB = (b.email || "").toLowerCase();
    return emailA.localeCompare(emailB);
  });
};

const handleEsc = (event) => {
  if (event.key === "Escape") {
    if (isDeleteModalOpen.value) {
      isDeleteModalOpen.value = false;
    } else {
      isModalOpen.value = false;
    }
  }
};

// Toast notification
const showToast = (message) => {
  if (toastTimeoutId) clearTimeout(toastTimeoutId);
  toastMessage.value = message;
  toastTimeoutId = setTimeout(() => {
    toastMessage.value = "";
  }, 3000);
};

// Function to open contact book modal and load contacts
const openContactBook = () => {
  isModalOpen.value = !isModalOpen.value;

  if (isModalOpen.value) {
    searchKey.value = "";
    editingContactIndex.value = null;
    getAllContacts("");
  }
};

// Confirm selection and close modal
const confirmSelection = () => {
  isModalOpen.value = false;
};

// --- Edit Contact ---
const onEditContact = (contact, index) => {
  editingContactIndex.value = index;
  editForm.value = {
    name: contact.name || "",
    email: contact.email || "",
    id: contact.id || null,
  };
  editOriginalEmail.value = contact.email || "";
  editContactErrors.value = { name: "", email: "" };
};

const cancelEditContact = () => {
  editingContactIndex.value = null;
  editForm.value = { name: "", email: "", id: null };
  editContactErrors.value = { name: "", email: "" };
};

const onSaveEditContact = async () => {
  editContactErrors.value = { name: "", email: "" };
  let hasError = false;

  if (!editForm.value.name.trim()) {
    editContactErrors.value.name = "Name is required";
    hasError = true;
  }

  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!editForm.value.email.trim()) {
    editContactErrors.value.email = "Email is required";
    hasError = true;
  } else if (!emailPattern.test(editForm.value.email.trim())) {
    editContactErrors.value.email = "Invalid email format";
    hasError = true;
  }

  // Check for duplicate email (excluding the contact being edited)
  const emailLower = editForm.value.email.trim().toLowerCase();
  const isDuplicateEmail = allContacts.value.some(
    (c) =>
      c.email?.toLowerCase() === emailLower &&
      c.email?.toLowerCase() !== editOriginalEmail.value.toLowerCase(),
  );
  if (isDuplicateEmail) {
    editContactErrors.value.email = "Email already exists";
    hasError = true;
  }

  if (hasError) return;

  const updatedName = editForm.value.name.trim();
  const updatedEmail = editForm.value.email.trim();

  // Try API update if the contact has an ID (contact_book source)
  if (editForm.value.id) {
    try {
      const csrfToken = document.querySelector(
        'meta[name="csrf-token"]',
      )?.content;
      const resp = await fetch(`/api/contact_books/${editForm.value.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({
          contact_book: { name: updatedName, email: updatedEmail },
        }),
      });
      if (!resp.ok) {
        const errorData = await resp.json();
        console.error("Failed to update contact on server:", errorData);
      }
    } catch (err) {
      console.error("Error updating contact on server:", err);
    }
  }

  // Update the displayed list
  if (
    editingContactIndex.value !== null &&
    allContacts.value[editingContactIndex.value]
  ) {
    allContacts.value[editingContactIndex.value].name = updatedName;
    allContacts.value[editingContactIndex.value].email = updatedEmail;
  }

  // Update the selected contact if it is the one being edited
  if (emailInput.value.trim().toLowerCase() === editOriginalEmail.value.toLowerCase()) {
    nameInput.value = updatedName;
    emailInput.value = updatedEmail;
  }

  cancelEditContact();
  showToast("Contact updated successfully.");
};

// --- Delete Contact ---
const onDeleteContact = (contact, index) => {
  contactToDelete.value = contact;
  contactToDeleteIndex.value = index;
  isDeleteModalOpen.value = true;
};

const onConfirmDelete = async () => {
  if (!contactToDelete.value) return;

  const contact = contactToDelete.value;

  // Try API delete if the contact is from contact_books
  if (contact.id && contact.source === 'contact_book') {
    try {
      const csrfToken = document.querySelector(
        'meta[name="csrf-token"]',
      )?.content;
      const resp = await fetch(`/api/contact_books/${contact.id}`, {
        method: "DELETE",
        headers: {
          "X-CSRF-Token": csrfToken,
        },
      });
      if (!resp.ok) {
        console.error("Failed to delete contact on server");
      }
    } catch (err) {
      console.error("Error deleting contact on server:", err);
    }
  }

  const contactEmailLower = contact.email?.toLowerCase();
  // Remove from displayed list
  allContacts.value = allContacts.value.filter(
    (c) => c.email?.toLowerCase() !== contactEmailLower,
  );

  // Reset edit state if the deleted contact was being edited
  if (editingContactIndex.value === contactToDeleteIndex.value) {
    cancelEditContact();
  }

  isDeleteModalOpen.value = false;
  contactToDelete.value = null;
  contactToDeleteIndex.value = null;
  showToast("Contact deleted successfully.");
};

const forceCheckAll = () => {
  const components = document.querySelectorAll(
    "new-bcc-submitter-autocomplete-v2",
  );
  console.log(`Found ${components.length} BCC submitter components`);

  document
    .querySelectorAll('input[data-email-input="true"]')
    .forEach((input) => {
      const event = new Event("input", { bubbles: true });
      input.dispatchEvent(event);
    });
};

onMounted(() => {
  window.addEventListener("keydown", handleEsc);

  if (props.email) {
    emailInput.value = props.email;
    fetchContactByEmail(props.email);
  }

  getAllContacts("");

  console.log(
    "BCC Component mounted with inputNameEmail:",
    props.inputNameEmail,
  );

  setTimeout(() => {
    checkForDuplicateSubmitter();

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        // Skip checkbox-related mutations to prevent clearing BCC values
        if (mutation.target && mutation.target.type === "checkbox") {
          continue;
        }

        // Only trigger on childList changes (adding/removing items)
        // Skip attribute changes as they can be triggered by unrelated elements
        if (mutation.type === "childList") {
          // Check if the mutation is relevant to CC/BCC components
          const isRelevant =
            mutation.addedNodes.length > 0 || mutation.removedNodes.length > 0;
          if (isRelevant) {
            if (checkTimeoutId) clearTimeout(checkTimeoutId);
            checkTimeoutId = setTimeout(() => {
              forceCheckAll();
            }, 300);
            break;
          }
        }
      }
    });

    const form = document.querySelector("form");
    if (form) {
      observer.observe(form, {
        childList: true,
        subtree: true,
      });
    }

    return () => {
      if (observer) observer.disconnect();
    };
  }, 500);
});

onUnmounted(() => {
  window.removeEventListener("keydown", handleEsc);
  if (checkTimeoutId) {
    clearTimeout(checkTimeoutId);
  }
  if (toastTimeoutId) {
    clearTimeout(toastTimeoutId);
  }
});

watch(emailInput, () => {
  if (checkTimeoutId) clearTimeout(checkTimeoutId);
  checkTimeoutId = setTimeout(() => {
    checkForDuplicateSubmitter();
  }, 200);
});

watch(nameInput, () => {
  if (checkTimeoutId) clearTimeout(checkTimeoutId);

  checkTimeoutId = setTimeout(async () => {
    const contactExist = await checkDuplicateContactNames(nameInput.value);
    isDuplicateName.value = contactExist;
    inputBccErrors.value = contactExist ? ["This name is already in use."] : [];
  }, 700);
});

watch(isModalOpen, (newVal, oldVal) => {
  if (newVal && !oldVal && allContacts.value.length === 0) {
    getAllContacts("");
  }
});

watchWithFilter(
  searchKey,
  () => {
    getAllContacts(searchKey.value);
  },
  {
    eventFilter: debounceFilter(500, { maxWait: 1000 }),
  },
);

const getAllContacts = async (text) => {
  const q = text.split(/[;,]+/).pop().trim();
  const queryParams = new URLSearchParams({ q });

  const keyword = searchKey.value?.trim().toLowerCase() || "";

  try {
    const resp = await fetch("/submitters_autocomplete?" + queryParams);
    let fetched = await resp.json();

    const filtered = keyword
      ? fetched.filter(
          (c) =>
            (c.email && c.email.toLowerCase().includes(keyword)) ||
            (c.name && c.name.toLowerCase().includes(keyword)),
        )
      : fetched;

    allContacts.value = sortContacts(filtered);
  } catch (error) {
    console.error("Error fetching contacts:", error);
  }
};

const fetchContactByEmail = (email) => {
  if (!email) return;

  try {
    const normalizedEmail = email.trim().toLowerCase();

    const matched = allContacts.value.find(
      (contact) =>
        contact.email && contact.email.toLowerCase() === normalizedEmail,
    );

    if (matched) {
      emailInput.value = matched.email || "";
      nameInput.value = matched.name || "";
    } else {
      emailInput.value = email;
    }
  } catch (err) {
    console.error("Failed to fetch contact from localStorage:", err);
  }
};

const onSelectedContact = (contact, event) => {
  if (event.type === "click") isModalOpen.value = false;

  event.stopPropagation();

  nameInput.value = contact.name || "";
  emailInput.value = contact.email || "";
  nameTouched.value = true;
  emailTouched.value = true;

  // Check for duplicate after selecting a contact
  checkForDuplicateSubmitter();
};

const checkForDuplicateSubmitter = () => {
  isDuplicateSubmitter.value = false;

  if (!emailInput.value || emailInput.value.trim() === "") {
    return;
  }

  const normalizedEmail = emailInput.value.trim().toLowerCase();
  // BCC component uses 'real_bcc_recipients', so check for that
  const isBccRecipient = props.inputNameEmail?.includes("real_bcc_recipients");

  if (!isBccRecipient) {
    return;
  }

  // Find all hidden email inputs that belong to submitters/signers
  // These have names like 'emails', 'submission[1][submitters][][email]', etc.
  const allEmailInputs = Array.from(
    document.querySelectorAll(
      'input[type="hidden"][name*="email"], input[name="emails"]',
    ),
  );

  let duplicateFound = false;

  for (const input of allEmailInputs) {
    // Skip if it's a CC/BCC input (we only want signer inputs)
    if (
      input.name?.includes("bcc_recipients") ||
      input.name?.includes("real_bcc_recipients")
    ) {
      continue;
    }

    // Check if it's a submitter/signer input
    const isSubmitterInput =
      input.name === "emails" ||
      input.name?.includes("submitters") ||
      input.name === "names";

    if (
      isSubmitterInput &&
      input.value?.trim().toLowerCase() === normalizedEmail
    ) {
      duplicateFound = true;
      break;
    }
  }

  isDuplicateSubmitter.value = duplicateFound;
};

const checkDuplicateContactNames = async (name) => {
  const normalizedName = name?.trim().toLowerCase();
  try {
    await getAllContacts(name);
    return allContacts.value.some(
      (contact) => contact.name?.trim().toLowerCase() === normalizedName,
    );
  } catch (e) {
    console.error("Error fetching contacts", e);
    return false;
  }
};

const validateInputs = () => {
  const errors = [];

  if (!nameInput.value.trim()) {
    errors.push("Name is required.");
  }

  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailPattern.test(emailInput.value.trim())) {
    errors.push("Email is required.");
  }

  inputBccErrors.value = errors;
  return errors.length === 0;
};

const onSubmitNewContact = () => {
  inputContactErrors.value = {
    name: "",
    email: "",
  };

  let hasError = false;
  if (!newContact.value.name) {
    inputContactErrors.value.name = "Name is required";
    hasError = true;
  }

  if (!newContact.value.email) {
    inputContactErrors.value.email = "Email is required";
    hasError = true;
  }

  if (hasError) return;

  const emailLower = newContact.value.email.trim().toLowerCase();
  const nameLower = newContact.value.name.trim().toLowerCase();

  // Only check duplicates against DB-sourced contacts (contact_book and contact)
  const dbContacts = allContacts.value.filter(
    (c) => c.source === "contact_book" || c.source === "contact",
  );

  const isDuplicateName = dbContacts.some(
    (contact) => contact.name?.toLowerCase() === nameLower,
  );
  const isDuplicateEmail = dbContacts.some(
    (contact) => contact.email?.toLowerCase() === emailLower,
  );

  if (isDuplicateName) {
    inputContactErrors.value.name = "Name already exists";
  }

  if (isDuplicateEmail) {
    inputContactErrors.value.email = "Email already exists";
  }

  if (isDuplicateName || isDuplicateEmail) return;

  // Remove any temporary (Not saved) contacts with the same email or name before saving
  allContacts.value = allContacts.value.filter(
    (c) =>
      c.source === "contact_book" ||
      c.source === "contact" ||
      (c.email?.toLowerCase() !== emailLower &&
        c.name?.toLowerCase() !== nameLower),
  );

  // Save to database via contact_books API
  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
  const newContactData = {
    name: newContact.value.name.trim(),
    email: newContact.value.email.trim(),
  };

  fetch('/api/contact_books', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken,
    },
    body: JSON.stringify({ contact_book: newContactData }),
  }).then(resp => resp.json()).then(async data => {
    if (data.contact?.id) {
      await getAllContacts(searchKey.value);
      showToast("Contact added successfully.");
    }
  }).catch(err => console.error('Failed to save contact to DB:', err));

  newContact.value = { name: "", email: "" };
};

const onDeleteTempContact = (index) => {
  const contact = allContacts.value[index];
  if (!contact) return;

  tempContactToDelete.value = contact;
  tempContactToDeleteIndex.value = index;
  isDeleteTempModalOpen.value = true;
};

const onConfirmDeleteTemp = () => {
  if (tempContactToDeleteIndex.value === null) return;

  allContacts.value.splice(tempContactToDeleteIndex.value, 1);
  showToast("Temporary contact removed.");

  isDeleteTempModalOpen.value = false;
  tempContactToDelete.value = null;
  tempContactToDeleteIndex.value = null;
};

const refreshLocalContacts = async () => {
  await getAllContacts(searchKey.value);
  showToast("Contacts refreshed from server.");
};
</script>

<style scoped>
.toast-fade-enter-active,
.toast-fade-leave-active {
  transition:
    opacity 0.3s ease,
    transform 0.3s ease;
}
.toast-fade-enter-from,
.toast-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
